import { useCallback, useMemo } from 'react'

import type { StandardRecipeItem } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { useBasket } from 'routes/Menu/domains/basket'

type UseRecipeGridBasketOptions = {
  onRecipeAdded?: (recipeId: RecipeUUID) => void
}

export const useRecipeGridBasket = (
  recipes: StandardRecipeItem[],
  { onRecipeAdded }: UseRecipeGridBasketOptions = {},
) => {
  const {
    addRecipeByUuid,
    canAddRecipes,
    getQuantitiesForRecipeId,
    reachedLimit,
    removeRecipeByUuid,
  } = useBasket()

  const recipeById = useMemo(
    () =>
      Object.fromEntries(recipes.map((recipe) => [recipe.id, recipe])) as Partial<
        Record<RecipeUUID, StandardRecipeItem>
      >,
    [recipes],
  )

  const getRecipeTileState = useCallback(
    (recipeId: RecipeUUID) => ({
      isInBasket: getQuantitiesForRecipeId(recipeId) > 0,
      canAddRecipes,
      reachedLimit,
    }),
    [canAddRecipes, getQuantitiesForRecipeId, reachedLimit],
  )

  const onAddClick = useCallback(
    (recipeId: RecipeUUID, recipePosition: number) => {
      const recipe = recipeById[recipeId]

      if (!recipe?.isAvailable) {
        return
      }

      addRecipeByUuid(recipeId, 'view_recipe_list_screen', {
        position: recipePosition.toString(),
      })
      onRecipeAdded?.(recipeId)
    },
    [addRecipeByUuid, onRecipeAdded, recipeById],
  )

  const onRemoveClick = useCallback(
    (recipeId: RecipeUUID, recipePosition: number) => {
      removeRecipeByUuid(recipeId, 'grid', recipePosition.toString())
    },
    [removeRecipeByUuid],
  )

  return {
    getRecipeTileState,
    onAddClick,
    onRemoveClick,
  }
}
